import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Menu, X } from "lucide-react";
import logo from "../logo_shopkabot.svg";
import { reportAdsConversion } from "../utilities/googleAds.utility";

const PRODUCT_NAME = "ShopKaBot";
const WHATSAPP_NUMBER = "919768260471";
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
  `Hey, I want to know more about ShopKaBot ${PRODUCT_NAME}.`,
)}`;

const NAV_LINKS = [
  { label: "Features", href: "#features" },
  { label: "How it works", href: "#solutions" },
  // Routes kept disabled while the public app is limited to /, /buy and /original-dashboard.
  // { label: "Services", href: "/services" },
  // { label: "Blog", href: "/blogs" },
  // { label: "Tools", href: "/tools" },
  // { label: "Pricing", href: "#pricing" },
  { label: "Buy", href: "/buy" },
  { label: "FAQ", href: "#faq" },
];

function PrimaryButton({
  children,
  onClick,
  href,
  target,
  rel,
  className = "",
}: {
  children: React.ReactNode;
  onClick?: (
    e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) => void;
  href?: string;
  target?: string;
  rel?: string;
  className?: string;
}) {
  const cls = [
    "inline-flex items-center justify-center gap-2",
    "h-11 md:h-14 px-4 md:px-8 text-sm md:text-base",
    "rounded-full",
    "bg-emerald-600 text-white",
    "font-semibold shadow-sm transition-all duration-200",
    "hover:bg-emerald-700 hover:shadow-md hover:-translate-y-[1px]",
    "active:translate-y-0 active:shadow-sm",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 focus-visible:ring-offset-2 focus-visible:ring-offset-white",
    "disabled:opacity-60 disabled:pointer-events-none",
    className,
  ].join(" ");

  if (href) {
    return (
      <a
        href={href}
        target={target}
        rel={rel}
        onClick={onClick}
        className={cls}
      >
        {children}
      </a>
    );
  }

  return (
    <button type="button" onClick={onClick} className={cls}>
      {children}
    </button>
  );
}

function SecondaryButton({
  children,
  onClick,
  href,
  target,
  rel,
  className = "",
}: {
  children: React.ReactNode;
  onClick?: (
    e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) => void;
  href?: string;
  target?: string;
  rel?: string;
  className?: string;
}) {
  const cls = [
    "inline-flex items-center justify-center gap-2",
    "h-11 md:h-14 px-4 md:px-8 text-sm md:text-base",
    "rounded-full",
    "border border-slate-200 bg-white text-slate-800",
    "font-semibold transition-all duration-200",
    "hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-800 hover:shadow-sm",
    "active:bg-emerald-100",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200 focus-visible:ring-offset-2 focus-visible:ring-offset-white",
    "disabled:opacity-60 disabled:pointer-events-none",
    className,
  ].join(" ");

  if (href) {
    return (
      <a
        href={href}
        target={target}
        rel={rel}
        onClick={onClick}
        className={cls}
      >
        {children}
      </a>
    );
  }

  return (
    <button type="button" onClick={onClick} className={cls}>
      {children}
    </button>
  );
}

export default function LandingNavbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const buyHref = useMemo(() => "/buy", []);
  const isLandingRoute = location.pathname === "/";

  const scrollToSection = (href: string) => {
    const id = href.replace("#", "");
    const el = document.getElementById(id);

    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      return true;
    }

    return false;
  };

  const handleNavigation = (href: string) => {
    if (href.startsWith("#")) {
      if (isLandingRoute && scrollToSection(href)) {
        return;
      }

      if (isLandingRoute) {
        window.location.hash = href;
        return;
      }

      navigate({ pathname: "/", hash: href });
      return;
    }

    navigate(href);
  };

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!isLandingRoute || !location.hash) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      scrollToSection(location.hash);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [isLandingRoute, location.hash]);

  return (
    <header className="sticky top-0 z-40 border-b border-slate-100 bg-white/80 backdrop-blur">
      <div className="mx-auto w-full max-w-6xl px-4">
        <div className="flex items-center justify-between py-3 md:py-4">
          <a href="/" className="inline-flex items-center gap-2 md:gap-3">
            <img
              src={logo}
              alt="ShopKaBot logo"
              className="h-10 w-10 rounded-lg object-contain md:h-14 md:w-14"
            />
            <span className="flex items-center gap-1 text-[16px] font-bold leading-none tracking-tight md:text-[20px]">
              <span className="text-slate-900">Shop</span>
              <span className="font-bold text-emerald-600">Ka</span>
              <span className="text-slate-900">Bot</span>
            </span>
          </a>

          <nav className="hidden items-center gap-6 text-sm font-medium text-slate-600 md:flex">
            {NAV_LINKS.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="transition hover:text-emerald-700"
                onClick={(e) => {
                  e.preventDefault();
                  handleNavigation(item.href);
                }}
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="hidden items-center gap-2 md:flex">
            <SecondaryButton
              href={buyHref}
              onClick={(e) => {
                e.preventDefault();
                navigate(buyHref);
              }}
            >
              Buy Now <ArrowRight className="h-4 w-4" />
            </SecondaryButton>

            <PrimaryButton
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => {
                e.preventDefault();
                reportAdsConversion({
                  onDone: () =>
                    window.open(WHATSAPP_LINK, "_blank", "noreferrer"),
                });
              }}
            >
              WhatsApp us <ArrowRight className="h-4 w-4" />
            </PrimaryButton>
          </div>

          <div className="flex items-center gap-2 md:hidden">
            <PrimaryButton
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noreferrer"
              className="px-3"
              onClick={(e) => {
                e.preventDefault();
                reportAdsConversion({
                  onDone: () =>
                    window.open(WHATSAPP_LINK, "_blank", "noreferrer"),
                });
              }}
            >
              WhatsApp <ArrowRight className="h-4 w-4" />
            </PrimaryButton>

            <button
              type="button"
              aria-label="Open menu"
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-900 shadow-sm transition active:scale-[0.98]"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <div
          className={[
            "overflow-hidden transition-[max-height,opacity] duration-200 md:hidden",
            open ? "max-h-96 opacity-100" : "max-h-0 opacity-0",
          ].join(" ")}
        >
          <div className="pb-4">
            <div className="rounded-2xl border border-slate-100 bg-white shadow-sm">
              <nav className="flex flex-col">
                {NAV_LINKS.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={(e) => {
                      e.preventDefault();
                      setOpen(false);
                      handleNavigation(item.href);
                    }}
                    className="px-4 py-3 text-sm font-medium text-slate-700 hover:bg-emerald-50 hover:text-emerald-800 first:rounded-t-2xl last:rounded-b-2xl"
                  >
                    {item.label}
                  </a>
                ))}
              </nav>

              <div className="flex flex-col gap-2 border-t border-slate-100 p-4">
                <SecondaryButton
                  href={buyHref}
                  onClick={(e) => {
                    e.preventDefault();
                    setOpen(false);
                    navigate(buyHref);
                  }}
                  className="w-full"
                >
                  Buy Now <ArrowRight className="h-4 w-4" />
                </SecondaryButton>

                <PrimaryButton
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full"
                  onClick={(e) => {
                    e.preventDefault();
                    setOpen(false);
                    reportAdsConversion({
                      onDone: () =>
                        window.open(WHATSAPP_LINK, "_blank", "noreferrer"),
                    });
                  }}
                >
                  WhatsApp us <ArrowRight className="h-4 w-4" />
                </PrimaryButton>
              </div>
            </div>
          </div>
        </div>
      </div>

      {open && (
        <button
          type="button"
          aria-label="Close menu"
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-30 bg-transparent md:hidden"
        />
      )}
    </header>
  );
}
